<template>
  <div>
    <Teleport to="#top-menu-bar" defer>
      <PageTopBar>
        <template #item-name>Import Galaxy Workflow Invocation</template>
      </PageTopBar>
    </Teleport>
    <div class="section">
      <div class="container">
        <div class="box">
          <!-- Galaxy Instance Selector -->
          <div class="field">
            <label class="label">Galaxy Instance</label>
            <div class="control">
              <SelectContainer
                v-model="selectedInstance"
                :options="galaxyInstances"
                track-by="id"
                label="label"
                :placeholder="
                  galaxyInstances.length === 0
                    ? 'No Galaxy instances configured'
                    : 'Select a Galaxy instance...'
                "
                :disabled="galaxyInstances.length === 0"
                :allow-empty="false"
                class="galaxy-instance-select"
              >
                <template #singleLabel="{ option }">
                  <div class="repository-select-option">
                    <span class="repository-select-option-name">
                      <span>{{ option.label }}</span>
                      <span class="has-text-grey is-size-7">
                        {{ option.url }}
                      </span>
                    </span>
                    <span class="repository-select-option-features">
                      <span class="icon is-small has-text-link">
                        <i class="fa-solid fa-sitemap" />
                      </span>
                    </span>
                  </div>
                </template>
                <template #option="{ option }">
                  <div class="repository-select-option">
                    <span class="repository-select-option-name">
                      <span>{{ option.label }}</span>
                      <span class="has-text-grey is-size-7">
                        {{ option.url }}
                      </span>
                    </span>
                    <span class="repository-select-option-features">
                      <span
                        title="Workflow import enabled"
                        class="icon is-small has-text-link"
                      >
                        <i class="fa-solid fa-sitemap" />
                      </span>
                    </span>
                  </div>
                </template>
              </SelectContainer>
            </div>
            <p class="help">
              Select the Galaxy instance where the workflow invocation is
              located
            </p>
          </div>

          <!-- Invocation URL -->
          <div class="field">
            <label class="label">Galaxy Workflow Invocation URL</label>
            <div class="control">
              <input
                v-model="invocationUrl"
                class="input"
                type="url"
                :placeholder="urlPlaceholder"
                :class="{ 'is-danger': hasError || !!hostMismatchError }"
                @blur="buildInvocationUrl"
              />
            </div>
            <p class="help">
              Paste the URL or the ID of a Galaxy workflow invocation to import
              it
            </p>
            <p v-if="hostMismatchError" class="help is-danger">
              {{ hostMismatchError }}
            </p>
            <p v-else-if="hasError" class="help is-danger">
              {{ errorMessage }}
            </p>
          </div>

          <div class="field">
            <div class="control">
              <button
                class="button is-primary"
                :class="{ 'is-loading': isImporting }"
                :disabled="!canImport"
                @click="importInvocation"
              >
                <span class="icon">
                  <i class="fas fa-download" />
                </span>
                <span>Import</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { post } from '@/api'
import { useAppConfig } from '@/composables/useAppConfig'
import { usePageShell } from '@/composables/usePageShell'
import { useStore } from '@/store'
import type { ApiError, WorkflowRepositoryOption } from '@/types/galaxy'

import PageTopBar from '@/components/PageTopBar.vue'
import SelectContainer from '@/components/SelectContainer.vue'

export default defineComponent({
  name: 'WorkflowRunImportGalaxyView',
  components: { PageTopBar, SelectContainer },
  setup() {
    usePageShell({ title: 'WorkflowRun - Galaxy Import' })
    const store = useStore()
    const router = useRouter()
    const { publicWorkflowRepositories } = useAppConfig()

    const invocationUrl = ref('')
    const isImporting = ref(false)
    const hasError = ref(false)
    const errorMessage = ref('')
    const selectedInstance = ref<WorkflowRepositoryOption | null>(null)

    const galaxyInstances = computed<WorkflowRepositoryOption[]>(() =>
      publicWorkflowRepositories.value.filter((repo) => repo.isGalaxy),
    )

    watch(
      galaxyInstances,
      (instances) => {
        if (instances.length > 0 && !selectedInstance.value) {
          selectedInstance.value = instances[0]
        }
      },
      { immediate: true },
    )

    const baseInstanceUrl = computed(() => {
      const url = selectedInstance.value?.url || ''
      return url ? `${url}/` : ''
    })

    const urlPlaceholder = computed(() =>
      selectedInstance.value
        ? `${selectedInstance.value.url}/workflows/invocations/<id>`
        : 'https://usegalaxy.eu/workflows/invocations/<id>',
    )

    // Pre-flight host match: the pasted URL must belong to the selected Galaxy
    // instance. Catches "picked Galaxy EMBL, pasted a usegalaxy.eu URL" before
    // we hit the server (which would otherwise fail opaquely).
    const hostMismatchError = computed<string | null>(() => {
      const instanceHost = selectedInstance.value?.urlObject?.hostname
      const url = invocationUrl.value?.trim()
      if (!instanceHost || !url) return null
      // Bare 16-hex id will be expanded by buildInvocationUrl on blur — don't warn on it
      if (/^[0-9a-f]{16}$/.test(url)) return null
      let inputHost: string
      try {
        inputHost = new URL(url).hostname
      } catch {
        return null
      }
      if (inputHost === instanceHost) return null
      return `URL host "${inputHost}" doesn't match the selected Galaxy server "${instanceHost}". Change the server dropdown, or paste a URL from ${instanceHost}.`
    })

    const canImport = computed(
      () =>
        !!selectedInstance.value &&
        !!invocationUrl.value &&
        !isImporting.value &&
        !hostMismatchError.value,
    )

    const buildInvocationUrl = (): void => {
      if (invocationUrl.value.trim().match(/^[0-9a-f]{16}$/)) {
        invocationUrl.value = `${
          baseInstanceUrl.value
        }workflows/invocations/${invocationUrl.value.trim()}`
      }
    }

    const importInvocation = async (): Promise<void> => {
      if (!invocationUrl.value || !selectedInstance.value) return
      isImporting.value = true
      hasError.value = false
      errorMessage.value = ''
      try {
        const response = await post(
          'workflows/workflowruns/import_from_galaxy/',
          {
            data: {
              url: invocationUrl.value,
              galaxy_server: selectedInstance.value?.url,
            },
          },
        )
        store.commit('notify', {
          type: 'success',
          message: 'Successfully imported Galaxy workflow invocation',
        })
        router.push({ path: `/${response.data.results?.id?.value}` })
      } catch (error) {
        hasError.value = true
        errorMessage.value =
          (error as ApiError)?.response?.data?.message ||
          'Failed to import workflow invocation'
        store.commit('notify', { type: 'error', message: errorMessage.value })
      } finally {
        isImporting.value = false
      }
    }

    return {
      invocationUrl,
      isImporting,
      hasError,
      errorMessage,
      hostMismatchError,
      selectedInstance,
      galaxyInstances,
      urlPlaceholder,
      canImport,
      importInvocation,
      buildInvocationUrl,
    }
  },
})
</script>

<style scoped>
.section {
  padding: 2rem 1.5rem;
}

.container {
  max-width: 800px;
}

.box {
  margin-top: 2rem;
}
</style>
